import type { ReactNode, HTMLAttributes } from "react";
import { columnDragHandleClassName, useColumnOrder } from "@/hooks/useColumnOrder";

export type ReorderableColumn<Row> = {
  id: string;
  header: ReactNode;
  renderCell: (row: Row) => ReactNode;
  className?: string;
  headerClassName?: string;
  fixed?: boolean;
  sortKey?: string;
};

type SortState = {
  column: string;
  direction: "asc" | "desc";
  onToggle: (column: string) => void;
};

type ReorderableTableProps<Row> = {
  pageKey: string;
  userId: number | string | null | undefined;
  columns: ReorderableColumn<Row>[];
  rows: Row[];
  getRowKey: (row: Row) => string | number;
  getRowProps?: (row: Row) => HTMLAttributes<HTMLTableRowElement>;
  sort?: SortState;
  tableClassName?: string;
  headClassName?: string;
  emptyMessage?: ReactNode;
  emptyClassName?: string;
};

/** Ordem visual no navegador por usuário/tela; a primeira coluna marcada como fixa não se move. */
export function ReorderableTable<Row>({
  pageKey,
  userId,
  columns,
  rows,
  getRowKey,
  getRowProps,
  sort,
  tableClassName = "w-full",
  headClassName = "bg-gray-50",
  emptyMessage,
  emptyClassName = "p-8 text-center text-muted-foreground",
}: ReorderableTableProps<Row>) {
  const { orderedColumns, getDragHandleProps } = useColumnOrder({
    pageKey,
    userId,
    columns: columns.map((column) => column.id),
    fixedColumns: columns.filter((column) => column.fixed).map((column) => column.id),
  });
  const columnsById = new Map(columns.map((column) => [column.id, column]));
  const ordered = orderedColumns.map((columnId) => columnsById.get(columnId)).filter((column): column is ReorderableColumn<Row> => Boolean(column));

  return (
    <table className={tableClassName}>
      <thead className={headClassName}>
        <tr>
          {ordered.map((column) => {
            const isSortable = Boolean(sort && column.sortKey);
            const isActive = isSortable && sort?.column === column.sortKey;
            return (
              <th key={column.id} className={column.headerClassName || "p-2 text-left text-sm font-medium"}>
                <span className="inline-flex items-center gap-1">
                  {isSortable ? (
                    <button
                      type="button"
                      className="cursor-pointer select-none hover:underline"
                      onClick={() => sort?.onToggle(column.sortKey!)}
                    >
                      {column.header}{isActive ? (sort?.direction === "asc" ? " ↑" : " ↓") : ""}
                    </button>
                  ) : column.header}
                  {!column.fixed && (
                    <span
                      {...getDragHandleProps(column.id)}
                      className={columnDragHandleClassName()}
                      title="Arraste para organizar a coluna"
                      aria-label="Arraste para organizar a coluna"
                    >
                      ⠿
                    </span>
                  )}
                </span>
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && emptyMessage ? (
          <tr><td colSpan={ordered.length} className={emptyClassName}>{emptyMessage}</td></tr>
        ) : rows.map((row) => {
          const rowProps = getRowProps?.(row) || {};
          return (
            <tr key={getRowKey(row)} {...rowProps}>
              {ordered.map((column) => (
                <td key={column.id} className={column.className || "p-2"}>{column.renderCell(row)}</td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
